import { useQuery } from '@tanstack/react-query';
import type { DmScansService } from './dmScansService';
import type { DmScanDetail, DmScanList, Morphing } from './types';

const SCANS_STALE_TIME_MS = 60 * 1000;
const DETAIL_STALE_TIME_MS = 30 * 60 * 1000;
const MORPHING_STALE_TIME_MS = 5 * 60 * 1000;

export interface DmScansQueryConfig {
    service: DmScansService;
    /** Root query-key segment; the dashboard's persister whitelist matches on it,
     *  so each host keeps its own rather than sharing one. */
    queryKeyRoot: string;
}

export function useDmScans(
    { service, queryKeyRoot }: DmScansQueryConfig,
    patientId: string | null | undefined,
    dmId: number | null | undefined
) {
    return useQuery<DmScanList | null>({
        queryKey: [queryKeyRoot, patientId, 'monitoring', 'scans'],
        queryFn: () => service.fetchDmScans(patientId as string),
        enabled: !!patientId && !!dmId,
        // Card reopen = fresh list; staleTime is a secondary floor for
        // re-renders that don't remount.
        staleTime: SCANS_STALE_TIME_MS,
        refetchOnMount: 'always',
        retry: 1
    });
}

export function useDmScanDetail(
    { service, queryKeyRoot }: DmScansQueryConfig,
    patientId: string | null | undefined,
    scanId: number | null | undefined
) {
    return useQuery<DmScanDetail | null>({
        queryKey: [queryKeyRoot, patientId, 'monitoring', 'scans', scanId],
        queryFn: () => service.fetchDmScanDetail(patientId as string, scanId as number),
        enabled: !!patientId && scanId != null,
        // Published scan photos are immutable.
        staleTime: DETAIL_STALE_TIME_MS,
        retry: 1
    });
}

export function useMorphing(
    { service, queryKeyRoot }: DmScansQueryConfig,
    patientId: string | null | undefined,
    enabled = true
) {
    return useQuery<Morphing>({
        queryKey: [queryKeyRoot, patientId, 'monitoring', 'morphing'],
        queryFn: () => service.fetchMorphing(patientId as string),
        enabled: !!patientId && enabled,
        staleTime: MORPHING_STALE_TIME_MS,
        retry: 1
    });
}
